import type { ReactNode } from "react";
import {
  INSTRUMENT_DEFINITIONS,
  type AnswerValue,
  type QuestionGroupField,
  type QuestionnaireItem,
} from "@ferrivia/api-contract";
import { useTranslation } from "@ferrivia/i18n";
import { useQuestionnaireCopy } from "@ferrivia/questionnaire-ui";
import { AnswerBody, QuestionBlock } from "./AnswerEntry";
import { useAnswerLabels } from "./useAnswerLabels";
import { isAnswered, itemsFor, type AnswerMap, type ItemFields } from "./answerMap";
import { articlesFor } from "./articles";
import { Disclosure } from "./Disclosure";
import { SlotArticle } from "./SlotArticle";
import { gateValue, groupHasContent, infusionHasContent, infusionsNewestFirst } from "./partVisibility";
import { INFUSION, SUPPLEMENTS_ARTICLE, SUPPLEMENT_GROUPS, type SupplementGroup } from "./summaryLayout";
import s from "./SupplementsPart.module.css";

const QUESTIONS = INSTRUMENT_DEFINITIONS.health_questionnaire.questions;

function groupFields(groupKey: string): readonly QuestionGroupField[] {
  const question = QUESTIONS.find((candidate) => candidate.key === groupKey);
  return question?.repeating ? question.fields : [];
}

function isDate(value: AnswerValue | undefined): value is Extract<AnswerValue, { kind: "date" }> {
  return value?.kind === "date";
}

function monthYear(iso: string): string {
  const day = new Date(`${iso}T00:00:00Z`);
  return `${day.getUTCMonth() + 1}/${day.getUTCFullYear()}`;
}

function useSubtitle() {
  const { t } = useTranslation("client");
  const month = monthYear;

  return (item: ItemFields, group: "current" | "past" | "ungrouped"): string | null => {
    const started = isDate(item.started_at) ? month(item.started_at.value) : null;
    const stopped = isDate(item.stopped_at) ? month(item.stopped_at.value) : null;

    if (group === "current") {
      return started ? t("summary.supplements.started", "Aloitettu {{date}}", { date: started }) : null;
    }
    if (!started && !stopped) {
      return null;
    }
    return `${started ?? ""} – ${stopped ?? ""}`.trim();
  };
}

function SupplementRow({ group, item, kind }: { group: SupplementGroup; item: ItemFields; kind: "current" | "past" | "ungrouped" }) {
  const { t } = useTranslation("client");
  const copy = useQuestionnaireCopy();
  const labels = useAnswerLabels();
  const subtitle = useSubtitle()(item, kind);
  const fields = groupFields(group.groupKey);

  const name = item.name?.kind === "text" ? item.name.value : "";
  const heading = (
    <span className={s.rowLabel}>
      <span className={s.rowTitle}>{name}</span>
      {subtitle && <span className={s.rowSubtitle}>{subtitle}</span>}
    </span>
  );

  const behind = group.behindRow.flatMap((key) => {
    const field = fields.find((candidate) => candidate.key === key);
    const value = item[key];
    return field && isAnswered(value) ? [{ field, value }] : [];
  });

  const labelFor = (field: QuestionGroupField) =>
    field.key === "side_effects"
      ? t("summary.supplements.sideEffects", "Vatsavaivoja tai muita sivuvaikutuksia")
      : copy.groupFieldFor(group.groupKey, field.key)?.label;

  const blocks = behind
    .filter(({ field }) => field.key !== "side_effects_detail")
    .map(({ field, value }) => {
      const detail = field.key === "side_effects" ? behind.find(({ field: other }) => other.key === "side_effects_detail") : undefined;
      return (
        <QuestionBlock key={field.key} label={labelFor(field)}>
          <AnswerBody value={value} labels={labels.forGroupField(group.groupKey, field, value)} />
          {detail && (
            <AnswerBody value={detail.value} labels={labels.forGroupField(group.groupKey, detail.field, detail.value)} />
          )}
        </QuestionBlock>
      );
    });

  return <ItemRow sectionId={group.id} heading={heading} blocks={blocks} />;
}

function ItemRow({ sectionId, heading, blocks }: { sectionId: "iron_supplements" | "other_supplements" | "iron_infusions"; heading: ReactNode; blocks: ReactNode[] }) {
  if (blocks.length === 0) {
    return <li className={`${s.row} ${s.plainRow}`}>{heading}</li>;
  }

  return (
    <li className={s.row}>
      <Disclosure sectionId={sectionId} foldedLabel={heading} openLabel={heading}>
        <div className={s.entries}>{blocks}</div>
      </Disclosure>
    </li>
  );
}

function ItemList({
  group,
  items,
  kind,
  title,
}: {
  group: SupplementGroup;
  items: ItemFields[];
  kind: "current" | "past" | "ungrouped";
  title?: string;
}) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className={s.group}>
      {title && <h4 className={s.groupTitle}>{title}</h4>}
      <ul className={s.list}>
        {items.map((item, index) => (
          <SupplementRow key={index} group={group} item={item} kind={kind} />
        ))}
      </ul>
    </div>
  );
}

function splitItems(group: SupplementGroup, items: ItemFields[]) {
  if (group.split === null) {
    return { current: items, past: [], ungrouped: [] };
  }
  const using = (item: ItemFields) => (item.still_using?.kind === "boolean" ? item.still_using.value : null);
  return {
    current: items.filter((item) => using(item) === true),
    past: items.filter((item) => using(item) === false),
    ungrouped: items.filter((item) => using(item) === null),
  };
}

function SupplementSection({ group, answers, items }: { group: SupplementGroup; answers: AnswerMap; items: readonly QuestionnaireItem[] }) {
  const { t } = useTranslation("client");
  const copy = useQuestionnaireCopy();
  const labels = useAnswerLabels();

  if (!groupHasContent(group, answers, items)) {
    return null;
  }

  const gate = answers[group.gate];
  const rows = itemsFor(items, group.groupKey);
  const split = splitItems(group, rows);

  return (
    <section className={s.section}>
      <h3 className={s.sectionTitle}>{copy.sectionFor(group.id)?.title}</h3>
      {gateValue(answers, group.gate) === false ? (
        <QuestionBlock label={copy.questionFor(group.gate)?.label}>
          <AnswerBody value={gate} labels={labels.forQuestion(group.gate, gate)} />
        </QuestionBlock>
      ) : (
        <>
          <ItemList group={group} items={split.current} kind="current" title={t("summary.supplements.current", "Käytössä nyt")} />
          <ItemList group={group} items={split.past} kind="past" title={t("summary.supplements.past", "Käytetty aiemmin")} />
          <ItemList group={group} items={split.ungrouped} kind="ungrouped" />
        </>
      )}
    </section>
  );
}

function InfusionRow({ item }: { item: ItemFields }) {
  const { t } = useTranslation("client");
  const copy = useQuestionnaireCopy();
  const labels = useAnswerLabels();
  const fields = groupFields(INFUSION.groupKey);

  const product = item.product?.kind === "choice" ? item.product.value : null;
  const other = item.product_other?.kind === "text" && isAnswered(item.product_other) ? item.product_other.value : null;
  const title =
    (product === "other_or_unknown" && other) ||
    (product && copy.groupFieldOptionLabelFor(INFUSION.groupKey, "product", product)) ||
    other ||
    t("summary.infusions.untitled", "Rautainfuusio");
  const subtitle = isDate(item.infusion_date) ? monthYear(item.infusion_date.value) : null;

  const heading = (
    <span className={s.rowLabel}>
      <span className={s.rowTitle}>{title}</span>
      {subtitle && <span className={s.rowSubtitle}>{subtitle}</span>}
    </span>
  );

  const blocks = INFUSION.behindRow.flatMap((key) => {
    const field = fields.find((candidate) => candidate.key === key);
    const value = item[key];
    return field && isAnswered(value)
      ? [
          <QuestionBlock key={key} label={copy.groupFieldFor(INFUSION.groupKey, key)?.label}>
            <AnswerBody value={value} labels={labels.forGroupField(INFUSION.groupKey, field, value)} />
          </QuestionBlock>,
        ]
      : [];
  });

  return <ItemRow sectionId="iron_infusions" heading={heading} blocks={blocks} />;
}

function InfusionSection({ answers, items }: { answers: AnswerMap; items: readonly QuestionnaireItem[] }) {
  const copy = useQuestionnaireCopy();
  const labels = useAnswerLabels();

  if (!infusionHasContent(answers, items)) {
    return null;
  }

  const gate = answers[INFUSION.gate];
  const details = answers[INFUSION.details];
  const rows = infusionsNewestFirst(itemsFor(items, INFUSION.groupKey));

  return (
    <section className={s.section}>
      <h3 className={s.sectionTitle}>{copy.sectionFor("iron_infusions")?.title}</h3>
      {gateValue(answers, INFUSION.gate) === false ? (
        <QuestionBlock label={copy.questionFor(INFUSION.gate)?.label}>
          <AnswerBody value={gate} labels={labels.forQuestion(INFUSION.gate, gate)} />
        </QuestionBlock>
      ) : rows.length === 0 ? (
        // A submission from before the structured group: the free text was the whole answer.
        isAnswered(details) && <AnswerBody value={details} labels={labels.forQuestion(INFUSION.details, details)} />
      ) : (
        <>
          <ul className={s.list}>
            {rows.map((item, index) => (
              <InfusionRow key={index} item={item} />
            ))}
          </ul>
          {isAnswered(details) && (
            <QuestionBlock label={copy.questionFor(INFUSION.details)?.label}>
              <AnswerBody value={details} labels={labels.forQuestion(INFUSION.details, details)} />
            </QuestionBlock>
          )}
        </>
      )}
    </section>
  );
}

export function SupplementsPart({ answers, items }: { answers: AnswerMap; items: readonly QuestionnaireItem[] }) {
  return (
    <div className={s.root}>
      {SUPPLEMENT_GROUPS.map((group) => (
        <SupplementSection key={group.id} group={group} answers={answers} items={items} />
      ))}
      <InfusionSection answers={answers} items={items} />
      {articlesFor(SUPPLEMENTS_ARTICLE, answers).map((slot) => (
        <SlotArticle key={slot.id} slot={slot} />
      ))}
    </div>
  );
}
